import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import { useApi } from "./ApiContext"; // Assuming ApiContext provides the api function

const DataContext = createContext(null);

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children, appId, initialData }) => {
	const { api } = useApi();
	const [appData, setAppData] = useState({});
	const [teamData, setTeamData] = useState({});
	const [canvasData, setCanvasData] = useState({});
	const [productData, setProductData] = useState([]);
	const [telemetryData, setTelemetryData] = useState([]);
	const [loading, setLoading] = useState({ app: true, team: true, canvas: true, products: true, telemetry: true });
	const [error, setError] = useState({ app: null, team: null, canvas: null, products: null, telemetry: null });
	const [buildPollingInterval, setBuildPollingInterval] = useState(null);

	const fetchData = useCallback(
		async (endpoint, setData, loadingKey, errorKey) => {
			if (!appId) return; // Don't fetch if appId isn't available yet
			console.log("DataProvider: fetching data", { endpoint, appId, initialData });
			// If we have i	itial data and this is the first app data fetch, skip it

			setLoading((prev) => ({ ...prev, [loadingKey]: true }));
			setError((prev) => ({ ...prev, [errorKey]: null }));

			try {
				// Construct the correct URL based on the endpoint
				let url;
				switch (endpoint) {
					case "app":
						url = `/app?appId=${appId}`; // Assuming this is the correct endpoint for single app data
						break;
					case "team":
						url = `/team?appId=${appId}`; // Assuming team data is fetched based on appId
						break;
					case "canvas":
						url = `/canvas?projectId=${appId}`; // Assuming canvas data is fetched based on appId
						break;
					case "products":
						// We need the tenantId to fetch products
						if (!appData?.services?.find((s) => s.type === "accounts")?.config?.realmId) {
							throw new Error("Tenant ID not available for products");
						}
						url = `/app?resource=products&appId=${appId}`; // Fetch products with tenant ID
						break;
					case "telemetry":
						// tenantId will be checked on the backend via checkTeamMembership
						url = `/app?resource=telemetry&appId=${appId}`;
						break;
					default:
						throw new Error(`Unknown endpoint: ${endpoint}`);
				}

				const response = await api(url);
				switch (endpoint) {
					case "app":
						// Only update if data has changed to prevent unnecessary re-renders
						setAppData((prevData) => {
							// Simple comparison of key fields that commonly change
							const hasChanged =
								!prevData ||
								prevData.buildStatus !== response.buildStatus ||
								prevData.buildDate !== response.buildDate ||
								prevData.buildNumber !== response.buildNumber ||
								prevData.updatedAt !== response.updatedAt ||
								JSON.stringify(prevData.services) !== JSON.stringify(response.services);

							if (hasChanged) {
								console.log("App data changed, updating state");
								return response;
							} else {
								console.log("App data unchanged, skipping update");
								return prevData;
							}
						});
						break;
					case "team":
						setTeamData((prevData) => {
							// Compare team data
							const hasChanged =
								JSON.stringify(prevData) !==
								JSON.stringify(
									response.teams.map((team) => ({
										...team,
										services: team.services || {},
										role: team.role || "member",
									}))
								);

							if (hasChanged) {
								console.log("Team data changed, updating state");
								return response.teams.map((team) => ({
									...team,
									services: team.services || {},
									role: team.role || "member",
								}));
							} else {
								console.log("Team data unchanged, skipping update");
								return prevData;
							}
						});
						break;
					case "canvas":
						setCanvasData((prevData) => {
							const hasChanged = JSON.stringify(prevData) !== JSON.stringify(response);

							if (hasChanged) {
								console.log("Canvas data changed, updating state");
								return response;
							} else {
								console.log("Canvas data unchanged, skipping update");
								return prevData;
							}
						});
						break;
					case "products":
						console.log("Products data", response);
						setProductData((prevData) => {
							const newData = Array.isArray(response) ? response : [];
							const hasChanged = JSON.stringify(prevData) !== JSON.stringify(newData);

							if (hasChanged) {
								console.log("Products data changed, updating state");
								return newData;
							} else {
								console.log("Products data unchanged, skipping update");
								return prevData;
							}
						});
						break;
					case "telemetry":
						console.log("Telemetry data", response);
						setTelemetryData((prevData) => {
							const newData = Array.isArray(response) ? response : [];
							const hasChanged = JSON.stringify(prevData) !== JSON.stringify(newData);

							if (hasChanged) {
								console.log("Telemetry data changed, updating state");
								return newData;
							} else {
								console.log("Telemetry data unchanged, skipping update");
								return prevData;
							}
						});
						break;
				}
			} catch (err) {
				console.error(`Error fetching ${endpoint} data:`, err);
				setError((prev) => ({ ...prev, [errorKey]: err.message || "Failed to fetch data" }));
				setData(endpoint === "products" || endpoint === "telemetry" ? [] : null); // Clear data on error, empty array for products and telemetry
			} finally {
				setLoading((prev) => ({ ...prev, [loadingKey]: false }));
			}
		},
		[api, appId, appData]
	);

	// Fetch all data when appId changes
	useEffect(() => {
		if (appId) {
			setAppData(initialData || {});
			console.log(initialData.services);
			console.log("DataProvider: appId", appId);
			fetchData("app", setAppData, "app", "app");
			fetchData("team", setTeamData, "team", "team");
			fetchData("canvas", setCanvasData, "canvas", "canvas");
			// Telemetry data will be fetched after appData is available and tenantId can be confirmed by backend
		} else {
			// Reset data if appId becomes null/undefined (e.g., navigating away)
			setAppData(null);
			setTeamData(null);
			setCanvasData(null);
			setProductData([]);
			setTelemetryData([]); // Reset telemetry data
			setLoading({ app: false, team: false, canvas: false, products: false, telemetry: false }); // Reset telemetry loading
			setError({ app: null, team: null, canvas: null, products: null, telemetry: null }); // Reset telemetry error
		}
	}, [appId, initialData]); // Removed fetchData from dependencies here as it causes infinite loop with appData dependency inside it

	// Fetch product data after app data is loaded and contains tenantId
	useEffect(() => {
		if (appData && appData.services && appData.services.find((s) => s.type === "accounts")?.config?.realmId) {
			fetchData("products", setProductData, "products", "products");
			fetchData("telemetry", setTelemetryData, "telemetry", "telemetry"); // Fetch telemetry data here
		} else {
			console.log("DataProvider: appData or tenantId not yet available for products/telemetry fetch", appData);
		}
	}, [appData, fetchData]);

	// Polling system for in-progress builds
	useEffect(() => {
		const inProgressStates = ["queue", "BUILDING", "provisioning", "building", "uploading", "FAILED", "COMPLETED"];
		const terminalStates = ["COMPLETED", "FAILED"];
		const currentBuildStatus = appData?.buildStatus;

		// Clear existing interval first
		if (buildPollingInterval) {
			clearInterval(buildPollingInterval);
			setBuildPollingInterval(null);
		}

		// Set up polling if build is in progress (but not in terminal states)
		if (currentBuildStatus && inProgressStates.includes(currentBuildStatus) && !terminalStates.includes(currentBuildStatus)) {
			console.log(`Setting up build polling for status: ${currentBuildStatus}`);
			const intervalId = setInterval(() => {
				console.log("Polling build status...");
				fetchData("app", setAppData, "app", "app");
			}, 30000); // Poll every 30 seconds

			setBuildPollingInterval(intervalId);
		} else if (currentBuildStatus) {
			console.log(`Build status ${currentBuildStatus} - stopping polling`);
		}

		// Cleanup function
		return () => {
			if (buildPollingInterval) {
				clearInterval(buildPollingInterval);
			}
		};
	}, [appData?.buildStatus, fetchData]);

	// Cleanup polling on unmount
	useEffect(() => {
		return () => {
			if (buildPollingInterval) {
				console.log("Cleaning up build polling on unmount");
				clearInterval(buildPollingInterval);
			}
		};
	}, [buildPollingInterval]);

	// Function to manually refetch specific data
	const refetchData = useCallback(
		async (endpoint) => {
			switch (endpoint) {
				case "app":
					await fetchData("app", setAppData, "app", "app");
					break;
				case "team":
					await fetchData("team", setTeamData, "team", "team");
					break;
				case "canvas":
					await fetchData("canvas", setCanvasData, "canvas", "canvas");
					break;
				case "products":
					await fetchData("products", setProductData, "products", "products");
					break;
				case "telemetry":
					await fetchData("telemetry", setTelemetryData, "telemetry", "telemetry");
					break;
				default:
					console.warn(`Attempted to refetch unknown endpoint: ${endpoint}`);
			}
		},
		[fetchData]
	);

	// Function to manually update appData
	const updateAppData = useCallback((updates) => {
		setAppData((prevData) => {
			const newData = {
				...prevData,
				...updates,
			};

			// If buildStatus was updated, the polling effect will trigger automatically
			// due to the dependency on appData?.buildStatus
			return newData;
		});
	}, []);

	const value = {
		appData,
		teamData,
		canvasData,
		productData,
		telemetryData,
		loading,
		error,
		refetchData,
		updateAppData,
	};

	return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
